import { defineComponent as _defineComponent } from 'vue'
import type { WebLocale } from '@/enums/web-locale';
import type { HrefLangTag } from '@/@types/graphql-types';
import type { Locale } from '@/helpers/locale-helper';
import type { SnowplowContext, SnowplowModuleContext } from '@/helpers/tracking/providers';

import CountryListItem from '@/components/modals/country-list/CountryListItem.vue';
import SearchBar from '@/components/search/SearchBar.vue';

import { WEB_LOCALES } from '@/constants/web-locales.constant';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ref, computed } from 'vue';
import { useLanguageStore, useRoutingStore } from '@/helpers/composables/useStores';
import { useRouter } from 'vue-router/composables';

import { useI18n } from '@/helpers/composables/useI18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'CountryList',
  props: {
    additionalContexts: { default: () => [] }
  },
  emits: ["selected-country"],
  setup(__props: any, { emit }: { emit: ({ (e: 'selected-country', webLocale: WebLocale): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { additionalContexts: (SnowplowModuleContext | SnowplowContext)[] };




const { tc } = useI18n();
const { setWebLocale, loadLocales, locales, webLocale, setLanguageWhenUserDidNot } = useLanguageStore();

loadLocales();

const router = useRouter();
const { urlMetadata } = useRoutingStore();

const search = ref('');

const localesWithPages = computed<Record<string, string>>(() =>
	Object.fromEntries(
		(urlMetadata.value?.allHrefLangTags ?? []).map((tl: HrefLangTag) => [tl.href.slice(1, 3), tl.href])
	)
);

const availableWebLocale = computed(() =>
	locales.value.map((locale: Locale) => locale.webLocale).includes(webLocale.value) ? webLocale.value : ''
);

const webLocales = computed(() => {
	const filteredLocales = (locales.value as Locale[])
		.filter(locale => {
			if (!(locale.webLocale in localesWithPages.value)) return false;
			if (!search.value) return true;

			return Object.values(locale.country_names)
				.map(name => name.toLowerCase())
				.concat(locale.country.toLowerCase())
				.some(name => name.includes(search.value.toLowerCase()));
		})
		.map(locale => locale.webLocale);

	return filteredLocales.sort((a, b) =>
		tc(`WEBAPP_COUNTRY_${a.toUpperCase()}`).localeCompare(tc(`WEBAPP_COUNTRY_${b.toUpperCase()}`))
	);
});

async function selectWebLocale(selectedLocale: WebLocale) {
	if (selectedLocale === webLocale.value) return;

	const path = localesWithPages.value[selectedLocale];
	if (path == null) return;

	const localeString = WEB_LOCALES[selectedLocale] || '';
	const language = localeString.substring(0, 2);

	await setWebLocale(selectedLocale);
	await setLanguageWhenUserDidNot(language);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'country_selector_toggled',
			property: selectedLocale,
		},
		props.additionalContexts
	);

	await router.push({ path });
	emit('selected-country', selectedLocale);
}

return { __sfc: true,props, emit, tc, setWebLocale, loadLocales, locales, webLocale, setLanguageWhenUserDidNot, router, urlMetadata, search, localesWithPages, availableWebLocale, webLocales, selectWebLocale, CountryListItem, SearchBar }
}

})